import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    CalendarClock,
    CheckCircle2,
    ChevronRight,
    Clock,
} from "lucide-react";
import { useData } from "../context/DataContext";
import { todayStr } from "../lib/progress";
import { todayDay, getTodaysStudents } from "../lib/schedule";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import TodaySchedule from "./TodaySchedule";

/**
 * Shows urgency-ordered tasks: (1) students not yet logged today, then
 * (2) pending makeups. When both are zero, renders a celebratory All Clear card.
 */
export default function NeedsAttentionCard() {
    const { students, attendance } = useData();
    const today = todayStr();
    const day = todayDay();

    const [quickLogOpen, setQuickLogOpen] = useState(false);

    const pendingMakeupsCount = useMemo(
        () =>
            attendance.filter(
                (r) =>
                    r.status === "Excused" && !(r.remark || "").startsWith("Done"),
            ).length,
        [attendance],
    );

    const unloggedToday = useMemo(() => {
        const todays = getTodaysStudents(students, day);
        const loggedIds = new Set(
            attendance.filter((r) => r.date === today).map((r) => r.studentId),
        );
        return todays.filter((r) => !loggedIds.has(r.student.id));
    }, [students, attendance, day, today]);
    const unloggedCount = unloggedToday.length;

    /* ───────── All Clear positive state ───────── */
    if (pendingMakeupsCount === 0 && unloggedCount === 0) {
        return (
            <section
                className="bg-success-soft/60 border border-success-soft rounded-3xl card-soft p-5 sm:p-6 flex items-center gap-4"
                data-testid="all-clear-card"
            >
                <div className="w-12 h-12 rounded-2xl bg-success-soft text-success flex items-center justify-center shrink-0">
                    <CheckCircle2 size={24} strokeWidth={2.4} />
                </div>
                <div>
                    <div className="font-display font-bold text-lg uppercase tracking-tight text-success">
                        All Clear
                    </div>
                    <div className="text-sm text-success/80">
                        Everything logged for today. No pending makeups.
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section
            className="bg-warning-soft/60 border border-warning-soft rounded-3xl card-soft overflow-hidden"
            data-testid="needs-attention-card"
        >
            <div className="px-5 sm:px-6 py-4 flex items-center gap-3 border-b border-warning-soft/80">
                <div className="w-9 h-9 rounded-xl bg-warning-soft text-warning flex items-center justify-center shrink-0">
                    <AlertTriangle size={18} strokeWidth={2.6} />
                </div>
                <div>
                    <div className="font-display font-bold text-lg uppercase tracking-tight text-warning">
                        Needs Attention
                    </div>
                    <div className="text-xs text-warning/80">
                        {pendingMakeupsCount + unloggedCount} task
                        {pendingMakeupsCount + unloggedCount === 1 ? "" : "s"} waiting
                    </div>
                </div>
            </div>

            <div className="divide-y divide-amber-100/80">
                {/* (1) Most urgent: students still to log today */}
                {unloggedCount > 0 && (
                    <button
                        onClick={() => setQuickLogOpen(true)}
                        data-testid="needs-attention-unlogged"
                        className="focus-ring group w-full flex items-center justify-between gap-3 px-5 sm:px-6 py-4 hover:bg-warning-soft/40 transition-colors text-left"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-white border border-warning-soft text-[#3D6385] flex items-center justify-center">
                                <Clock size={16} strokeWidth={2.5} />
                            </div>
                            <div>
                                <div className="font-bold text-sm text-slate-900">
                                    {unloggedCount} student
                                    {unloggedCount === 1 ? "" : "s"} not logged today
                                </div>
                                <div className="text-xs text-slate-500">
                                    {unloggedToday
                                        .slice(0, 3)
                                        .map((r) => r.student.name.split(" ")[0])
                                        .join(", ")}
                                    {unloggedCount > 3 ? ` +${unloggedCount - 3}` : ""}
                                </div>
                            </div>
                        </div>
                        <ChevronRight
                            size={18}
                            strokeWidth={2.5}
                            className="text-warning/70 group-hover:text-warning"
                        />
                    </button>
                )}

                {/* (2) Pending makeups */}
                {pendingMakeupsCount > 0 && (
                    <Link
                        to="/makeups"
                        data-testid="needs-attention-makeups"
                        className="group flex items-center justify-between gap-3 px-5 sm:px-6 py-4 hover:bg-warning-soft/40 transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-white border border-warning-soft text-warning flex items-center justify-center">
                                <CalendarClock size={16} strokeWidth={2.5} />
                            </div>
                            <div>
                                <div className="font-bold text-sm text-slate-900">
                                    {pendingMakeupsCount} makeup
                                    {pendingMakeupsCount === 1 ? "" : "s"} pending
                                </div>
                                <div className="text-xs text-slate-500">
                                    Open the make-up summary to resolve
                                </div>
                            </div>
                        </div>
                        <ChevronRight
                            size={18}
                            strokeWidth={2.5}
                            className="text-warning/70 group-hover:text-warning"
                        />
                    </Link>
                )}
            </div>

            <Dialog open={quickLogOpen} onOpenChange={setQuickLogOpen}>
                <DialogContent
                    className="max-w-3xl rounded-3xl p-0 overflow-hidden max-h-[90vh] flex flex-col"
                    data-testid="needs-attention-quick-log"
                >
                    <DialogHeader className="px-6 sm:px-8 pt-6 pb-4 border-b border-mist bg-gradient-to-br from-action-soft to-white">
                        <DialogTitle className="font-display font-bold text-2xl uppercase tracking-tight">
                            Quick Log · {day}
                        </DialogTitle>
                        <DialogDescription className="text-sm text-slate-600">
                            Only showing students still pending today — log everyone in
                            seconds.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="px-6 sm:px-8 py-4 overflow-y-auto">
                        {/* Mount lazily so the auto-focus effect fires every time the dialog opens. */}
                        {quickLogOpen && (
                            <TodaySchedule
                                compact
                                filterUnlogged
                                autoFocusFirst
                            />
                        )}
                    </div>
                    <div className="px-6 sm:px-8 py-4 border-t border-mist flex justify-end bg-mist/50">
                        <Button
                            onClick={() => setQuickLogOpen(false)}
                            className="rounded-xl bg-[#3D6385] hover:bg-[#345570] text-white font-bold h-11 px-5"
                            data-testid="needs-attention-done-btn"
                        >
                            Done
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </section>
    );
}
